/**
 * ThomVerifiedStamp — official THOM VERIFIED™ certification mark.
 *
 * Renders the approved master brand asset stored at
 * `/brand/thom-verified-stamp.png`. This component NEVER redraws, recolours,
 * clips, distorts or animates the stamp; it is delivered pixel-for-pixel
 * from the source file. Dynamic certificate metadata (trust score, ID,
 * status) is displayed next to the stamp rather than overlaid on it.
 *
 * ── Rules enforced by this component ────────────────────────────────────
 *  • The stamp is shown ONLY when verificationStatus === "verified".
 *    Other states render a neutral status pill (never the stamp).
 *  • Aspect ratio is locked 1:1 with object-contain — never squashed.
 *  • Adequate padding is preserved so the silver outer ring is not clipped.
 *  • Corporate logo remains separate (see Navbar Shield mark).
 *
 * Approved sizes (px):
 *    xs = 32   → status indicator (tooltip required)
 *    sm = 56   → marketplace card
 *    md = 112  → vehicle detail & dashboards
 *    lg = 200  → certificates / Digital Passports
 *    xl = 320  → PDF covers / formal verification screens
 */
import { Link } from "react-router-dom";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

const SIZE_PX = { xs: 32, sm: 56, md: 112, lg: 200, xl: 320 };
const MASTER = "/brand/thom-verified-stamp.png";

const STATUS_PILL = {
  requested:   { label: "Verification Pending",   cls: "bg-amber-100  text-amber-800  border-amber-300" },
  assigned:    { label: "Inspection Scheduled",   cls: "bg-sky-100    text-sky-800    border-sky-300"   },
  in_progress: { label: "Review in Progress",     cls: "bg-sky-100    text-sky-800    border-sky-300"   },
  rejected:    { label: "Verification Failed",    cls: "bg-red-100    text-red-800    border-red-300"   },
  expired:     { label: "Certificate Expired",    cls: "bg-slate-100  text-slate-700  border-slate-300" },
  revoked:     { label: "Certificate Revoked",    cls: "bg-red-100    text-red-800    border-red-300"   },
  suspended:   { label: "Certificate Suspended",  cls: "bg-amber-100  text-amber-800  border-amber-300" },
  superseded:  { label: "Certificate Superseded", cls: "bg-slate-100  text-slate-700  border-slate-300" },
  unverified:  { label: "Not Yet Verified",       cls: "bg-slate-100  text-slate-700  border-slate-300" },
};

export const ThomVerifiedStamp = ({
  size = "md",
  className = "",
  certificateId = null,
  trustScore = null,
  rating = null,
  verificationStatus = "verified",
  verificationDate = null,
  expiryDate = null,
  clickable = false,
  showTooltip = true,
  showDetailsPanel = false,
  lazy = true,
}) => {
  const px = SIZE_PX[size] || (typeof size === "number" ? size : SIZE_PX.md);
  const isValid = verificationStatus === "verified";

  // Non-verified → neutral status pill (never the official stamp)
  if (!isValid) {
    const s = STATUS_PILL[verificationStatus] || STATUS_PILL.unverified;
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm mono uppercase tracking-widest text-[10px] font-semibold border ${s.cls} ${className}`}
        data-testid={`stamp-status-${verificationStatus}`}
      >
        {s.label}
      </span>
    );
  }

  const alt = [
    "THOM VERIFIED certification stamp",
    trustScore ? `Trust Score ${trustScore}` : null,
    certificateId ? `Certificate ${certificateId}` : null,
  ].filter(Boolean).join(", ");

  const img = (
    <img
      src={MASTER}
      alt={alt}
      width={px}
      height={px}
      loading={lazy ? "lazy" : "eager"}
      draggable={false}
      // padding preserves outer silver ring; object-contain prevents squashing
      className="block select-none"
      style={{
        width: px,
        height: px,
        aspectRatio: "1 / 1",
        objectFit: "contain",
      }}
    />
  );

  // Container preserves circular ring with breathing room
  const stampBlock = (
    <span
      className="inline-block relative"
      style={{ width: px, height: px }}
      data-testid="thom-verified-stamp"
    >
      {img}
    </span>
  );

  const withTooltip = showTooltip && !showDetailsPanel ? (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{stampBlock}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="mono text-[10px] uppercase tracking-widest text-primary">THOM VERIFIED™</div>
          {trustScore != null && <div className="text-xs">Trust Score {trustScore}{rating ? ` · ${rating}` : ""}</div>}
          {certificateId && <div className="text-xs mono">{certificateId}</div>}
          {verificationDate && <div className="text-[10px] text-muted-foreground">Verified {new Date(verificationDate).toLocaleDateString()}</div>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : stampBlock;

  const clickableBlock = clickable && certificateId ? (
    <Link
      to={`/verify/${certificateId}`}
      className="inline-block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-full"
      aria-label={alt}
      data-testid="thom-verified-stamp-link"
    >
      {withTooltip}
    </Link>
  ) : withTooltip;

  if (!showDetailsPanel) {
    return <span className={`inline-block ${className}`}>{clickableBlock}</span>;
  }

  // Full details panel (used on Vehicle Detail + Digital Passport + Certificates)
  return (
    <div className={`flex items-center gap-5 ${className}`} data-testid="thom-verified-stamp-panel">
      {clickableBlock}
      <div className="min-w-0">
        <div className="mono text-[10px] uppercase tracking-widest text-primary">THOM VERIFIED™</div>
        <div className="display text-2xl leading-tight">
          {trustScore != null ? `Trust Score ${trustScore}` : "Certified"}
          {rating && <span className="text-muted-foreground text-base font-normal"> · {rating}</span>}
        </div>
        <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
          {certificateId && <div className="mono">Certificate: <span className="text-foreground">{certificateId}</span></div>}
          {verificationDate && <div>Verified: {new Date(verificationDate).toLocaleDateString()}</div>}
          {expiryDate && <div>Valid until: {new Date(expiryDate).toLocaleDateString()}</div>}
          {certificateId && (
            <Link to={`/verify/${certificateId}`} className="inline-block mt-1 text-primary hover:underline text-xs" data-testid="stamp-verify-link">
              Verify certificate →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThomVerifiedStamp;
